import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Building2, User, Mail, Phone, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Wholesale = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/wholesale-inquiries`, formData);
      toast.success('Inquiry submitted successfully! We will contact you soon.');
      setFormData({
        company_name: '',
        contact_person: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="wholesale-page" className="min-h-screen pt-32 pb-24">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4 font-medium">For Businesses</p>
          <h1 className="font-display text-5xl md:text-6xl font-normal tracking-tight text-foreground mb-6">
            Wholesale <span className="italic">Inquiries</span>
          </h1>
          <p className="text-lg font-light leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            Partner with us for bulk orders and exclusive wholesale rates. We supply premium fragrances to businesses worldwide.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
        >
          <div className="text-center p-6 border border-white/5">
            <Building2 className="mx-auto text-primary mb-4" size={32} strokeWidth={1.5} />
            <h3 className="font-display text-xl mb-2">Bulk Orders</h3>
            <p className="text-sm text-muted-foreground font-light">
              Competitive pricing for large quantity orders
            </p>
          </div>
          <div className="text-center p-6 border border-white/5">
            <MessageSquare className="mx-auto text-primary mb-4" size={32} strokeWidth={1.5} />
            <h3 className="font-display text-xl mb-2">Custom Solutions</h3>
            <p className="text-sm text-muted-foreground font-light">
              Tailored product offerings for your business
            </p>
          </div>
          <div className="text-center p-6 border border-white/5">
            <Send className="mx-auto text-primary mb-4" size={32} strokeWidth={1.5} />
            <h3 className="font-display text-xl mb-2">Global Shipping</h3>
            <p className="text-sm text-muted-foreground font-light">
              Reliable delivery to destinations worldwide
            </p>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          onSubmit={handleSubmit}
          data-testid="wholesale-form"
          className="border border-white/10 p-8 md:p-12 space-y-8"
        >
          <h2 className="font-display text-3xl mb-8">Get in Touch</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="text-sm uppercase tracking-widest font-medium block mb-3 flex items-center space-x-2">
                <Building2 size={16} className="text-primary" strokeWidth={1.5} />
                <span>Company Name *</span>
              </label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                required
                data-testid="wholesale-company-input"
                className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest font-medium block mb-3 flex items-center space-x-2">
                <User size={16} className="text-primary" strokeWidth={1.5} />
                <span>Contact Person *</span>
              </label>
              <input
                type="text"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                required
                data-testid="wholesale-contact-input"
                className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest font-medium block mb-3 flex items-center space-x-2">
                <Mail size={16} className="text-primary" strokeWidth={1.5} />
                <span>Email Address *</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="wholesale-email-input"
                className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
              />
            </div>

            <div>
              <label className="text-sm uppercase tracking-widest font-medium block mb-3 flex items-center space-x-2">
                <Phone size={16} className="text-primary" strokeWidth={1.5} />
                <span>Phone Number *</span>
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                data-testid="wholesale-phone-input"
                className="w-full bg-transparent border-b border-white/20 rounded-none px-0 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-sm uppercase tracking-widest font-medium block mb-3 flex items-center space-x-2">
              <MessageSquare size={16} className="text-primary" strokeWidth={1.5} />
              <span>Your Message *</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows={6}
              data-testid="wholesale-message-input"
              placeholder="Tell us about your requirements, expected quantity, and any specific needs..."
              className="w-full bg-transparent border border-white/20 px-4 py-4 focus:border-primary focus:ring-0 placeholder:text-white/30 text-lg transition-colors duration-300 focus:outline-none resize-none"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="wholesale-submit-button"
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-4 uppercase tracking-widest text-sm font-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3"
          >
            <span>{loading ? 'Sending...' : 'Submit Inquiry'}</span>
            <Send size={18} strokeWidth={1.5} />
          </button>
        </motion.form>
      </div>
    </div>
  );
};